// composables/useCommon.js
import {computed, getCurrentInstance, ref} from 'vue';
import {callApi as apiCall} from '@/api';
import LogService from '@/services/LogService';
import {camelCase} from 'lodash';
import {addAmPm, getLanguageFromLanguageParam} from '@/common/common';

export function useCommon() {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error('useCommon must be called within setup()');
  }

  const { proxy } = instance; // Zugriff auf this.$store, this.$route, this.$router, this.$wpAPI

  // reactive state
  const str = ref({});

  // computed properties
  const lang = computed(() => proxy.$store.state.lang);
  const phoneCodes = computed(() => [
    { code: '49', text: '+49', country: 'Deutschland', flag: '' },
  ]);
  const routeLang = computed(() => proxy.$route.params?.lang || '');

  // methods
  async function getWordPressStrings(sectionArray, pageTitleSection = undefined, selector = 'title', l = undefined) {
    LogService.log('calling wp strings');
    await proxy.$wpAPI.getStringsArray(
      sectionArray,
      l ?? routeLang.value
    ).then((results) => {
      LogService.log('wp strings returned', results);
      str.value = results;
      if (pageTitleSection !== undefined) {
        const pageTitle = str.value[camelCase(pageTitleSection)][selector];
        proxy.$store.commit('SET_PAGE_TITLE', pageTitle);
        LogService.log('getWordPressStrings, set pageTitle', pageTitle);
      }
    });
  }

  async function saveBeforeLeave() {
    const infoApi = '/api/order/';
    await callApi({ url: infoApi, method: 'post' }, proxy.$store);
  }

  function callApi(o) {
    LogService.log('callApi', o);
    return apiCall(o, proxy.$store);
  }

  function checkIfRedirectToEnglishNecessary() {
    const isBot = () => {
      if (typeof window === 'undefined') return false;
      const userAgent = navigator.userAgent.toLowerCase();
      const bots = [
        'bot',
        'googlebot',
        'crawler',
        'spider',
        'robot',
        'bingbot',
        'google-inspectiontool',
        'lighthouse',
        'facebookexternalhit',
        'twitterbot'
      ];
      return new RegExp(bots.join('|'), 'i').test(userAgent);
    };
    if (isBot()) return false;

    const { locale } = Intl.NumberFormat().resolvedOptions();
    const supportedLocales = navigator.languages;
    const { lang: routeLangParam } = proxy.$route.params;
    const { just_arrived } = proxy.$route.query;
    const deSupported = supportedLocales.some((x) => x.startsWith('de'));

    if (just_arrived && locale?.startsWith('en') && !deSupported && routeLangParam === 'de') {
      const { query } = proxy.$route;
      if ('just_arrived' in query) delete query.just_arrived;
      proxy.$router.replace({ name: proxy.$route.name, params: { ...proxy.$route.params, lang: 'en' }, query });
      return true;
    }
    return false;
  }

  function format(template, ...args) {
    let n = -1;
    return template?.replace(/(%s)/g, () => {
      n += 1;
      return args[n];
    });
  }

  function formatHourAmPm(hour, paramLang) {
    const l = getLanguageFromLanguageParam(paramLang);
    return l === 'en' ? addAmPm(hour) : String(hour);
  }

  function formatAdjacentHours(start, end, paramLang) {
    const l = getLanguageFromLanguageParam(paramLang);
    if (l === 'en') {
      return format(str.value.buy?.between_hours_adjacent, addAmPm(start), addAmPm(end));
    }
    return format(str.value.buy?.between_hours_adjacent, start, end);
  }

  function getDiffInDays(date1, date2) {
    const oneDay = 24 * 60 * 60 * 1000;
    const diffInTime = Math.abs(date2.getTime() - date1.getTime());
    return Math.ceil(diffInTime / oneDay);
  }

  return {
    str,
    lang,
    phoneCodes,
    routeLang,
    getWordPressStrings,
    saveBeforeLeave,
    callApi,
    checkIfRedirectToEnglishNecessary,
    format,
    formatHourAmPm,
    formatAdjacentHours,
    getDiffInDays,
  };
}
